<script lang="ts" setup>
import { useElementSize } from "@vueuse/core";
import type { AxiosProgressEvent } from "axios";
import type { SerializedEditorState } from "lexical";
import { computed, getCurrentInstance, nextTick, onMounted, onUnmounted, ref, watch } from "vue";

import { backendOld } from "~/api";
import ChatPanelHeader from "~/components/chat/ChatPanelHeader.vue";
import ChatSkillSuggestions from "~/components/chat/ChatSkillSuggestions.vue";
import ChatAndSkillEditor from "~/components/text/ChatAndSkillEditor.vue";
import type { Chat, ChatMessage } from "~/shared/types";
import { useAppStore, useDataStore } from "~/stores";
import { makeDuid, makeEmptyLexicalState } from "~/utils/common";
import { when } from "~/utils/wait";

const currentInstance = getCurrentInstance();
const appStore = useAppStore();
const dataStore = useDataStore();

const messagesWrapper = ref<HTMLDivElement | null>(null);
const oldEditors = ref<HTMLDivElement[]>([]);
const messageEditors = ref<InstanceType<typeof ChatAndSkillEditor>[]>([]);
const streamingEditor = ref<InstanceType<typeof ChatAndSkillEditor> | null>(null);
const draftMessageEditor = ref<InstanceType<typeof ChatAndSkillEditor> | null>(null);
const contentReady = ref(true);

const chat = ref<Chat | null>(null);
const isWorking = ref(false);
const manuallyStreaming = ref(false);
const shouldAutoScroll = ref(true);
const isStreaming = computed(() => manuallyStreaming.value || (streamingEditor.value?.isStreaming ?? false));

const lastUserEditor = computed(() => {
  const lastUserMessageIndex = chat.value?.messages
    .slice(0, oldEditors.value.length)
    .findLastIndex((e) => !e.isAssistant);
  if (lastUserMessageIndex === undefined || lastUserMessageIndex === -1) {
    return null;
  }
  return oldEditors.value[lastUserMessageIndex];
});
const { height: wrapperH } = useElementSize(messagesWrapper);
const { height: lastH } = useElementSize(lastUserEditor);

const lastValidBottomOffset = ref<string | undefined>(undefined);
const bottomOffset = computed(() => {
  if (!wrapperH.value || !lastH.value || !lastUserEditor.value) {
    return lastValidBottomOffset.value;
  }

  const fromColumnTop = lastUserEditor.value.offsetTop + lastH.value;
  const belowLastUser = wrapperH.value - fromColumnTop;
  const gap = wrapperH.value - lastH.value - belowLastUser;
  return `${Math.max(gap, 0)}px`;
});

watch(bottomOffset, (newVal, oldVal) => {
  if (!newVal || newVal === oldVal) {
    return;
  }
  lastValidBottomOffset.value = newVal;
});

const makeEmptyDraft = () => ({
  duid: makeDuid(),
  content: makeEmptyLexicalState(),
  extras: chat.value?.messages.findLast((e) => !e.isAssistant)?.extras ?? [],
  isAssistant: false,
  createdAt: new Date().toISOString(),
});

const draft = computed(() => (!chat.value || !chat.value.draft ? makeEmptyDraft() : chat.value.draft));

const allEditorsReady = computed(
  () => messageEditors.value.every((e) => e.isReady) && draftMessageEditor.value?.isReady
);

const handleUserScroll = () => {
  if (!messagesWrapper.value) {
    return;
  }

  const { scrollTop, scrollHeight, clientHeight } = messagesWrapper.value;
  const isAtBottom = scrollTop + clientHeight >= scrollHeight;
  shouldAutoScroll.value = isAtBottom && (isStreaming.value || manuallyStreaming.value);
};

const handleMouseDown = (e: MouseEvent) => {
  if (messagesWrapper.value && e.offsetX > messagesWrapper.value.clientWidth) {
    handleUserScroll();
  }
};

const scrollOnStreamUpdate = async () => {
  if (!shouldAutoScroll.value || !messagesWrapper.value) {
    return;
  }
  messagesWrapper.value.scrollTo({ top: messagesWrapper.value.scrollHeight, behavior: "smooth" });
};

const scrollToBottom = async () => {
  if (!messagesWrapper.value) {
    return;
  }
  contentReady.value = false;
  await nextTick();
  await when(allEditorsReady);
  if (!messagesWrapper.value) {
    return;
  }

  const { scrollHeight, clientHeight } = messagesWrapper.value;
  messagesWrapper.value.scrollTop = scrollHeight - clientHeight;
  contentReady.value = true;
};

const resetLoadingState = () => {
  manuallyStreaming.value = false;
  isWorking.value = false;
};

const sendMessage = async (message: ChatMessage) => {
  const chatAtSend = chat.value;
  if (!chatAtSend) {
    return;
  }

  shouldAutoScroll.value = true;
  isWorking.value = true;

  dataStore.addMessageToChat(chatAtSend.duid, {
    ...message,
    isAssistant: false,
  });

  const handlePartialResponse = async (e: AxiosProgressEvent) => {
    if (chatAtSend?.duid !== chat.value?.duid) {
      return;
    }

    isWorking.value = false;
    const { currentTarget } = e.event;

    if (!currentTarget || currentTarget.status < 200 || currentTarget.status >= 300 || currentTarget.response === "") {
      return;
    }

    manuallyStreaming.value = true;
    streamingEditor.value?.streamText(currentTarget.response);
  };

  try {
    await backendOld.chat.streamMessage(message, chatAtSend.duid, handlePartialResponse);
  } catch (error) {
    return;
  } finally {
    if (chatAtSend.duid === chat.value?.duid) {
      resetLoadingState();
      draftMessageEditor.value?.focus();
    }
  }
};

const sendContent = async (content: SerializedEditorState) => {
  await sendMessage({ ...draft.value, content });
};

const closeChat = () => {
  appStore.setChatPanelOpen(false);
};

const selectChat = async (chatDuid: string, newChat?: Chat) => {
  if (chat.value?.duid === chatDuid) {
    return;
  }

  const selectedChat = newChat ?? dataStore.getChatByDuid(chatDuid);
  if (!selectedChat) {
    return;
  }

  chat.value = selectedChat;
  draftMessageEditor.value?.focus();
  appStore.setLastChat(chat.value);
  resetLoadingState();
};

const createNewChat = async () => {
  if (chat.value && dataStore.isChatEmpty(chat.value)) {
    await draftMessageEditor.value?.syncExtras(false);
    draftMessageEditor.value?.focus();
    return;
  }

  const newChat = await dataStore.createChat({
    duid: makeDuid(),
    draft: { ...makeEmptyDraft(), duid: makeDuid() },
  });
  if (!newChat) {
    return;
  }

  chat.value = newChat;
  await nextTick();
  draftMessageEditor.value?.focus();
  appStore.setLastChat(newChat);
  resetLoadingState();
};

const selectLatestOrCreateNew = async () => {
  const lastUpdatedChat = dataStore.chatList[0];
  if (lastUpdatedChat && dataStore.isChatEmpty(lastUpdatedChat)) {
    await draftMessageEditor.value?.syncExtras(false);
    selectChat(lastUpdatedChat.duid);
    return;
  }

  if (!appStore.lastChat) {
    await createNewChat();
    return;
  }

  selectChat(appStore.lastChat.duid, appStore.lastChat);
};

const deleteChat = (chatDuid: string) => {
  const chatToDelete = dataStore.getChatByDuid(chatDuid);
  if (!chatToDelete) {
    return;
  }

  dataStore.deleteChat(chatToDelete);
  if (chat.value?.duid === chatDuid) {
    createNewChat();
  }
};

const sendContentInNewChat = async (message: SerializedEditorState) => {
  await createNewChat();
  await sendContent(message);
};

onMounted(async () => {
  await selectLatestOrCreateNew();
  await scrollToBottom();
});

onMounted(() => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  appStore.chatPanel = (currentInstance?.exposeProxy ?? currentInstance?.exposed ?? null) as any;
});

onUnmounted(() => {
  appStore.chatPanel = null;
});

defineExpose({
  createNewChat,
  sendContentInNewChat,
});
</script>

<template>
  <div class="flex size-full flex-col items-center border-l bg-std border-md">
    <!-- Header -->
    <ChatPanelHeader
      v-if="chat"
      :chat="chat"
      @close="closeChat"
      @create="createNewChat"
      @select="selectChat($event)"
      @delete="deleteChat" />

    <template v-if="chat">
      <!-- Messages -->
      <div
        v-if="chat.messages.length > 0"
        :key="chat.duid"
        ref="messagesWrapper"
        :class="[
          'relative flex w-full max-w-3xl grow pl-[15px] text-sm text-md',
          contentReady ? 'overflow-y-scroll' : 'overflow-hidden pr-[15px]',
        ]"
        @touchmove="handleUserScroll"
        @wheel="handleUserScroll"
        @mousedown="handleMouseDown">
        <div class="flex size-full flex-col gap-4" :style="{ marginBottom: bottomOffset }">
          <div
            v-for="(message, i) in chat.messages"
            v-show="!(isStreaming && !isWorking && message.isAssistant && i === chat.messages.length - 1)"
            ref="oldEditors"
            :key="message.duid"
            class="flex w-full"
            :class="message.isAssistant ? 'justify-start' : 'justify-end rounded-md'">
            <ChatAndSkillEditor
              ref="messageEditors"
              :chat="chat"
              :message="message"
              :editor-classes="message.isAssistant ? 'px-1' : 'px-2 ml-8 max-w-xl al border bg-lt border-md'" />
          </div>

          <div v-if="isWorking || isStreaming" class="relative flex w-full justify-start">
            <ChatAndSkillEditor
              ref="streamingEditor"
              :chat="chat"
              editor-classes="px-1 bg-std"
              @stream-updated="scrollOnStreamUpdate" />
            <!-- prettier-ignore -->
            <div
              v-if="isWorking"
              class="animate-expand absolute left-1 top-2.5 flex size-3 justify-start rounded-full bg-md" />
          </div>
        </div>
      </div>

      <!-- Draft -->
      <div
        class="flex w-full max-w-3xl flex-col justify-between gap-4 pb-3"
        :class="chat.messages.length > 0 ? 'px-2.5 pt-2' : 'h-full overflow-y-auto px-[15px]'">
        <ChatAndSkillEditor
          ref="draftMessageEditor"
          :chat="chat"
          :message="draft"
          is-draft
          editor-classes="pt-1"
          @send="sendMessage" />
        <ChatSkillSuggestions v-if="chat.messages.length === 0" @send="sendContent" />
      </div>
    </template>
  </div>
</template>
